import moment from 'moment';

import {
  CurrencyBoostType,
  CurrencyUnlimitedBoostType,
  GachaAmountMode,
  GachaAppVersionOperator,
  GachaBoxInput,
  GachaBoxType,
  GachaCampaignDataFragment,
  GachaCampaignStatus,
  GachaCountRowDataFragment,
  GachaCountRowInput,
  GachaCurrency,
  GachaMatcherDataFragment,
  GachaMatcherInput,
  GachaMysteryInput,
  GachaNamespace,
  GachaPoolRowDataFragment,
  GachaPoolRowInput,
  GachaRecoveryMode,
  GachaRefreshMode,
  GachaRewardType,
  GachaTier,
  UpdateGachaCampaignInput,
  UserSpendTier,
  UserSubscriptionTier,
} from '../../../../../__gqltypes__';
import { userIdsArrayToString, userIdsStringToArray } from '../../general/EventMatchers';

export type GachaCampaignData = GachaCampaignDataFragment;
type GachaBoxData = GachaCampaignData['boxes'][0];
type GachaMysteryData = NonNullable<GachaBoxData['mystery']>;

export const GACHA_BASE_URL = '/sp3/liveops/gacha-shop';

// ---------------------------------------------------------------------------------------------------------------------
// Labels and display helpers
// ---------------------------------------------------------------------------------------------------------------------

// The status is server-derived (DISABLED, then SCHEDULED / ENDED by the UTC window, else RUNNING).
export const GACHA_STATUS_VARIANT: Record<GachaCampaignStatus, string> = {
  [GachaCampaignStatus.DISABLED]: 'secondary',
  [GachaCampaignStatus.SCHEDULED]: 'info',
  [GachaCampaignStatus.RUNNING]: 'success',
  [GachaCampaignStatus.ENDED]: 'dark',
};

export function formatUtc(value: number | string | null | undefined): string {
  if (value == null) {
    return '—';
  }
  return moment.utc(value).format('YYYY-MM-DD HH:mm:ss [UTC]');
}

// ----- the campaign window in the admin's own timezone (display only: the stored value stays UTC) ------------------

// "GMT+7", "GMT+5:30", "GMT-3", "GMT+0", from the offset moment reports for the browser's zone at that instant.
function gmtLabel(local: moment.Moment): string {
  const offset = local.utcOffset();
  const sign = offset < 0 ? '-' : '+';
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  return `GMT${sign}${hours}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}`;
}

// The same instant in the browser's timezone: "= Thu 15 Oct 2026, 22:45 (GMT+7)". Empty for an invalid value.
export function formatGachaLocalTime(epochMs: number): string {
  if (!Number.isFinite(epochMs)) {
    return '';
  }
  const local = moment(epochMs);
  return `= ${local.format('ddd D MMM YYYY, HH:mm')} (${gmtLabel(local)})`;
}

// "2d 3h", "7h 2m", "3h", "12m", "less than a minute".
function formatGachaSpan(ms: number): string {
  const span = moment.duration(Math.abs(ms));
  const days = Math.floor(span.asDays());
  const hours = span.hours();
  const minutes = span.minutes();
  if (days > 0) {
    return hours ? `${days}d ${hours}h` : `${days}d`;
  }
  if (hours > 0) {
    return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  return minutes > 0 ? `${minutes}m` : 'less than a minute';
}

// "starts in 7h 2m" / "started 3h ago", "ends in 2d 3h" / "ended 5m ago". Empty for an invalid value.
export function formatGachaRelative(epochMs: number, nowMs: number, edge: 'start' | 'end'): string {
  if (!Number.isFinite(epochMs)) {
    return '';
  }
  const span = formatGachaSpan(epochMs - nowMs);
  if (epochMs > nowMs) {
    return `${edge === 'start' ? 'starts' : 'ends'} in ${span}`;
  }
  return `${edge === 'start' ? 'started' : 'ended'} ${span} ago`;
}

// The server's status rule (SP3BE gachaCampaignStatus) at a given instant, so the editor's badge follows the clock
// between server reads: DISABLED, then SCHEDULED / ENDED by the UTC window, else RUNNING.
export function gachaCampaignStatusAt(
  campaign: Pick<GachaCampaignData, 'enabled' | 'startDate' | 'stopDate' | 'permanent'>,
  nowMs: number
): GachaCampaignStatus {
  if (!campaign.enabled) {
    return GachaCampaignStatus.DISABLED;
  }
  const start = toEpochMs(campaign.startDate);
  if (start != null && nowMs < start) {
    return GachaCampaignStatus.SCHEDULED;
  }
  const stop = campaign.permanent ? null : toEpochMs(campaign.stopDate);
  if (stop != null && nowMs >= stop) {
    return GachaCampaignStatus.ENDED;
  }
  return GachaCampaignStatus.RUNNING;
}

// Same reorder helper as curation SongList.tsx (immutable).
export function reorderList<T>(list: ReadonlyArray<T>, startIndex: number, endIndex: number): T[] {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

// Server errors arrive as "CODE" or "CODE: details". Returns the code and the details.
export function parseGachaAdminError(message: string | null | undefined): { code: string | null; details: string[] } {
  const raw = (message ?? '').replace(/^GraphQL error:\s*/, '');
  const match = raw.match(/(GACHA_ADMIN_[A-Z_]+)(?::\s*(.*))?/);
  if (!match) {
    return { code: null, details: raw ? [raw] : [] };
  }
  const details = (match[2] ?? '')
    .split(' | ')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  return { code: match[1], details };
}

// ---------------------------------------------------------------------------------------------------------------------
// Option labels
// ---------------------------------------------------------------------------------------------------------------------

export const GACHA_NAMESPACE_LABELS: Record<GachaNamespace, string> = {
  [GachaNamespace.PROD]: 'PROD',
  [GachaNamespace.BETA]: 'BETA',
  [GachaNamespace.BOTH]: 'BOTH (PROD and BETA)',
};

/**
 * Lifetime spend tiers, ordered high spend -> low, then non-payers. Copied from the Sale Event screen (its list is
 * local to SaleEventData.tsx): same labels and USD ranges, so both screens name the same segments the same way.
 */
export const GACHA_SPEND_TIER_OPTIONS = [
  { tier: UserSpendTier.KRAKEN, label: 'Kraken', range: 'USD > 1,000' },
  { tier: UserSpendTier.WHALE, label: 'Whale', range: 'USD 100 - 1,000' },
  { tier: UserSpendTier.DOLPHIN, label: 'Dolphin', range: 'USD 20 - 100' },
  { tier: UserSpendTier.MINNOW, label: 'Minnow', range: 'USD 0 - 20' },
  { tier: UserSpendTier.NPAU, label: 'NPAU', range: 'never paid' },
] as const;

export const GACHA_VERSION_OPERATOR_LABELS: Record<GachaAppVersionOperator, string> = {
  [GachaAppVersionOperator.GTE]: '>=',
  [GachaAppVersionOperator.GT]: '>',
  [GachaAppVersionOperator.EQ]: '=',
  [GachaAppVersionOperator.LT]: '<',
  [GachaAppVersionOperator.LTE]: '<=',
};

export const GACHA_TIERS: GachaTier[] = [
  GachaTier.FREE,
  GachaTier.EPIC,
  GachaTier.ICONIC,
  GachaTier.LEGENDARY,
  GachaTier.MAESTRO,
];

export const GACHA_CURRENCY_LABELS: Record<GachaCurrency, string> = {
  [GachaCurrency.DIAMOND]: 'Diamonds',
  [GachaCurrency.COIN]: 'Coins',
  [GachaCurrency.ACCOLADE]: 'Accolades',
  [GachaCurrency.FAME]: 'Fames',
};

// Diamonds first: it is the default currency.
export const GACHA_CURRENCIES: GachaCurrency[] = [
  GachaCurrency.DIAMOND,
  GachaCurrency.COIN,
  GachaCurrency.ACCOLADE,
  GachaCurrency.FAME,
];

// The reward picker is restricted to the GachaRewardType enum (the server's allowlist).
export const GACHA_REWARD_TYPE_LABELS: Record<GachaRewardType, string> = {
  [GachaRewardType.CURRENCY_COIN]: 'Coins',
  [GachaRewardType.CURRENCY_DIAMOND]: 'Diamonds',
  [GachaRewardType.CURRENCY_BOOST]: 'Booster',
  [GachaRewardType.CURRENCY_UNLIMITED_BOOST]: 'Unlimited Booster',
  [GachaRewardType.CURRENCY_CATEGORY_FRAGMENT]: 'Fragments (category)',
  [GachaRewardType.CURRENCY_META_FRAGMENT_BRONZE]: 'Meta Fragments Bronze',
  [GachaRewardType.CURRENCY_META_FRAGMENT_SILVER]: 'Meta Fragments Silver',
  [GachaRewardType.CURRENCY_META_FRAGMENT_GOLD]: 'Meta Fragments Gold',
  [GachaRewardType.CURRENCY_SHARD_BRONZE]: 'Shards Bronze',
  [GachaRewardType.CURRENCY_SHARD_SILVER]: 'Shards Silver',
  [GachaRewardType.CURRENCY_SHARD_GOLD]: 'Shards Gold',
  [GachaRewardType.CONTENT_PACK_REWARD]: 'Playlist',
  [GachaRewardType.CURRENCY_VANITY_PIECE]: 'Vanity Pieces',
};

export const GACHA_REWARD_TYPES: GachaRewardType[] = Object.keys(GACHA_REWARD_TYPE_LABELS) as GachaRewardType[];

// FREE and MAESTRO tiers default to a FREE box, the other tiers to a PAID box (server rule).
export function defaultGachaBoxTypeForTier(tier: GachaTier): GachaBoxType {
  return tier === GachaTier.FREE || tier === GachaTier.MAESTRO ? GachaBoxType.FREE : GachaBoxType.PAID;
}

// Half-up, minimum 1: the server's discountedGachaAmount.
export function discountedGachaAmount(amount: number, percent: number): number {
  const pct = Math.min(100, Math.max(0, percent));
  return Math.max(1, Math.floor((amount * (100 - pct) + 50) / 100));
}

// ---------------------------------------------------------------------------------------------------------------------
// `Day, HH:MM:SS` durations (box refresh, mystery global recovery), stored as seconds
// ---------------------------------------------------------------------------------------------------------------------

const DURATION_PATTERN = /^\s*(\d+)\s*,\s*(\d{1,2}):(\d{1,2}):(\d{1,2})\s*$/;

const pad2 = (value: number) => String(value).padStart(2, '0');

export function formatGachaDuration(totalSeconds: number | null | undefined): string {
  if (totalSeconds == null || !Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return '';
  }
  const whole = Math.floor(totalSeconds);
  const days = Math.floor(whole / 86400);
  const hours = Math.floor((whole % 86400) / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const seconds = whole % 60;
  return `${days}, ${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;
}

// null = not a valid `Day, HH:MM:SS` (hours < 24, minutes and seconds < 60).
export function parseGachaDuration(text: string): number | null {
  const match = DURATION_PATTERN.exec(text ?? '');
  if (!match) {
    return null;
  }
  const [days, hours, minutes, seconds] = match.slice(1).map((part) => parseInt(part, 10));
  if (hours > 23 || minutes > 59 || seconds > 59) {
    return null;
  }
  return days * 86400 + hours * 3600 + minutes * 60 + seconds;
}

// ---------------------------------------------------------------------------------------------------------------------
// Editor drafts. `_clientId` is an editor-only stable identity (React keys, drag ids), stripped on save.
// ---------------------------------------------------------------------------------------------------------------------

let clientIdCounter = 0;
export function nextGachaClientId(): string {
  clientIdCounter += 1;
  return `gacha${clientIdCounter}`;
}

export type GachaRewardDraft = {
  type: GachaRewardType;
  // Booster type (CURRENCY_BOOST), category id (CURRENCY_CATEGORY_FRAGMENT), playlist id (CONTENT_PACK_REWARD) or
  // vanity piece id (CURRENCY_VANITY_PIECE: the piece type, from Vanity Pieces' catalog).
  itemId: string | null;
  unlimitedBoostType: CurrencyUnlimitedBoostType | null;
};

// The amount is EXACT or a RANGE, an explicit per-row toggle (PRA's PraPoolItemRow pattern, copied). The draft keeps
// all three fields so switching the mode does not lose what was typed; only the active mode's fields are sent.
export type GachaPoolRowDraft = {
  _clientId: string;
  reward: GachaRewardDraft;
  amountMode: GachaAmountMode;
  amount: number; // EXACT mode
  minAmount: number; // RANGE mode
  maxAmount: number; // RANGE mode
  weight: number;
};

export type GachaCountRowDraft = {
  _clientId: string;
  count: number;
  // Weight, normalised by the list's sum; 0 = this count never comes up.
  weight: number;
};

// Sale Event's matcher set (in its form order) minus "Has purchased subscription before", plus the build-version
// operator and Only Maestro. Empty = no filter.
export type GachaMatcherDraft = {
  // Registration window, epoch ms (UTC).
  userMinCreationDate: number | null;
  userMaxCreationDate: number | null;
  appVersionOperator: GachaAppVersionOperator;
  // Empty = no build version criterion.
  appVersion: string;
  // As typed. Empty = any level.
  userLevel: string;
  // null = All. One tier exactly, as in Sale Event.
  subscriptionTier: UserSubscriptionTier | null;
  // Comma-separated, as typed.
  userIds: string;
  // Empty = All users.
  spendTiers: UserSpendTier[];
  maestroOnly: boolean;
  // Comma-separated, as typed. Always wins.
  excludedUserIds: string;
};

export type GachaMysteryDraft = {
  enabled: boolean;
  // "Same as campaign": a real inheritance, resolved by the server against the campaign's current matcher.
  inherit: boolean;
  criteria: GachaMatcherDraft;
  firstRatePercent: number;
  decreasePercent: number;
  minRatePercent: number;
  recoveryMode: GachaRecoveryMode;
  recoveryIntervalSeconds: number | null;
  pool: GachaPoolRowDraft[];
};

export type GachaBoxDraft = {
  _clientId: string;
  // null = a new box; the server generates its id on save.
  id: string | null;
  tier: GachaTier;
  type: GachaBoxType;
  claimLimit: number;
  refreshMode: GachaRefreshMode;
  refreshIntervalSeconds: number | null;
  priceCurrency: GachaCurrency;
  priceAmount: number;
  discountEnabled: boolean;
  discountPercent: number;
  // null = the campaign start / end.
  discountStartDate: number | null;
  discountStopDate: number | null;
  countTable: GachaCountRowDraft[];
  rewards: GachaPoolRowDraft[];
  allowDuplicates: boolean;
  reminderEnabled: boolean;
  // null = no mystery reward configured.
  mystery: GachaMysteryDraft | null;
};

export type GachaCampaignDraft = {
  id: string;
  name: string;
  enabled: boolean;
  namespace: GachaNamespace;
  startDate: number;
  // null = permanent.
  stopDate: number | null;
  showCountdown: boolean;
  startNotificationEnabled: boolean;
  matcher: GachaMatcherDraft;
  boxes: GachaBoxDraft[];
};

const ONE_DAY_SECONDS = 86400;

export const GACHA_DEFAULT_PRICE_AMOUNT = 100;

export function newGachaMatcherDraft(): GachaMatcherDraft {
  return {
    userMinCreationDate: null,
    userMaxCreationDate: null,
    appVersionOperator: GachaAppVersionOperator.GTE,
    appVersion: '',
    userLevel: '',
    subscriptionTier: null,
    userIds: '',
    spendTiers: [],
    maestroOnly: false,
    excludedUserIds: '',
  };
}

export function newGachaPoolRow(): GachaPoolRowDraft {
  return {
    _clientId: nextGachaClientId(),
    reward: { type: GachaRewardType.CURRENCY_COIN, itemId: null, unlimitedBoostType: null },
    amountMode: GachaAmountMode.EXACT,
    amount: 10,
    minAmount: 10,
    maxAmount: 10,
    weight: 10,
  };
}

export function newGachaCountRow(count = 1, weight = 100): GachaCountRowDraft {
  return { _clientId: nextGachaClientId(), count, weight };
}

export function newGachaMysteryDraft(): GachaMysteryDraft {
  return {
    enabled: true,
    inherit: true,
    criteria: newGachaMatcherDraft(),
    firstRatePercent: 100,
    decreasePercent: 10,
    minRatePercent: 50,
    recoveryMode: GachaRecoveryMode.GLOBAL,
    recoveryIntervalSeconds: ONE_DAY_SECONDS,
    pool: [newGachaPoolRow()],
  };
}

export function newGachaBoxDraft(tier: GachaTier = GachaTier.FREE): GachaBoxDraft {
  return {
    _clientId: nextGachaClientId(),
    id: null,
    tier,
    type: defaultGachaBoxTypeForTier(tier),
    claimLimit: 1,
    refreshMode: GachaRefreshMode.CUSTOM,
    refreshIntervalSeconds: ONE_DAY_SECONDS,
    priceCurrency: GachaCurrency.DIAMOND,
    priceAmount: GACHA_DEFAULT_PRICE_AMOUNT,
    discountEnabled: false,
    discountPercent: 10,
    discountStartDate: null,
    discountStopDate: null,
    countTable: [newGachaCountRow()],
    rewards: [newGachaPoolRow()],
    allowDuplicates: false,
    reminderEnabled: true,
    mystery: null,
  };
}

// ----- server → draft ----------------------------------------------------------------------------------------------

const toEpochMs = (value: unknown): number | null => {
  if (value == null) {
    return null;
  }
  const ms = typeof value === 'number' ? value : new Date(value as string).getTime();
  return Number.isFinite(ms) ? ms : null;
};

export function toGachaMatcherDraft(matcher: GachaMatcherDataFragment | null | undefined): GachaMatcherDraft {
  if (!matcher) {
    return newGachaMatcherDraft();
  }
  return {
    userMinCreationDate: toEpochMs(matcher.userMinCreationDate),
    userMaxCreationDate: toEpochMs(matcher.userMaxCreationDate),
    appVersionOperator: matcher.appVersionOperator ?? GachaAppVersionOperator.GTE,
    appVersion: matcher.appVersion ?? '',
    userLevel: matcher.userLevel != null ? String(matcher.userLevel) : '',
    subscriptionTier: matcher.subscriptionTier ?? null,
    userIds: userIdsArrayToString(matcher.userIds),
    spendTiers: [...matcher.spendTiers],
    maestroOnly: matcher.maestroOnly,
    excludedUserIds: userIdsArrayToString(matcher.excludedUserIds),
  };
}

export function toGachaPoolRowDrafts(rows: ReadonlyArray<GachaPoolRowDataFragment>): GachaPoolRowDraft[] {
  return rows.map((row) => ({
    _clientId: nextGachaClientId(),
    reward: {
      type: row.reward.type,
      itemId: row.reward.itemId ?? null,
      unlimitedBoostType: row.reward.unlimitedBoostType ?? null,
    },
    // The server sends RANGE for a row saved before the toggle existed, min === max included.
    amountMode: row.amountMode,
    amount: row.amount ?? row.minAmount ?? 1,
    minAmount: row.minAmount ?? row.amount ?? 1,
    maxAmount: row.maxAmount ?? row.amount ?? 1,
    weight: row.weight,
  }));
}

export function toGachaCountRowDrafts(rows: ReadonlyArray<GachaCountRowDataFragment>): GachaCountRowDraft[] {
  return rows.map((row) => ({ _clientId: nextGachaClientId(), count: row.count, weight: row.weight }));
}

function toGachaMysteryDraft(mystery: GachaMysteryData): GachaMysteryDraft {
  return {
    enabled: mystery.enabled,
    inherit: mystery.matcher.inherit,
    criteria: toGachaMatcherDraft(mystery.matcher.criteria),
    firstRatePercent: mystery.firstRatePercent,
    decreasePercent: mystery.decreasePercent,
    minRatePercent: mystery.minRatePercent,
    recoveryMode: mystery.recoveryMode,
    recoveryIntervalSeconds: mystery.recoveryIntervalSeconds ?? null,
    pool: toGachaPoolRowDrafts(mystery.pool),
  };
}

function toGachaBoxDraft(box: GachaBoxData): GachaBoxDraft {
  return {
    _clientId: nextGachaClientId(),
    id: box.id,
    tier: box.tier,
    type: box.type,
    claimLimit: box.claimLimit,
    refreshMode: box.refreshMode,
    refreshIntervalSeconds: box.refreshIntervalSeconds ?? null,
    priceCurrency: box.price?.currency ?? GachaCurrency.DIAMOND,
    priceAmount: box.price?.amount ?? GACHA_DEFAULT_PRICE_AMOUNT,
    discountEnabled: !!box.discount,
    discountPercent: box.discount?.percent ?? 10,
    discountStartDate: toEpochMs(box.discount?.startDate),
    discountStopDate: toEpochMs(box.discount?.stopDate),
    countTable: toGachaCountRowDrafts(box.countTable),
    rewards: toGachaPoolRowDrafts(box.rewards),
    allowDuplicates: box.allowDuplicates,
    reminderEnabled: box.reminderEnabled,
    mystery: box.mystery ? toGachaMysteryDraft(box.mystery) : null,
  };
}

// ---------------------------------------------------------------------------------------------------------------------
// New-campaign defaults. The server creates a campaign starting now, permanent, start push on; the admin saves these
// over it right after the creation. TEST (and a local server) gets a short window so QA can see it start and end.
// ---------------------------------------------------------------------------------------------------------------------

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

// The backend this admin talks to, decided as config.ts picks it: REACT_APP_NAMESPACE=test is TEST, and a
// development build without the beta namespace talks to localhost:8085. Everything else is BETA/PROD.
export function isGachaTestBackend(
  namespace: string | undefined = process.env.REACT_APP_NAMESPACE,
  nodeEnv: string | undefined = process.env.NODE_ENV
): boolean {
  if (namespace === 'test') {
    return true;
  }
  const isProdBuild = !nodeEnv || nodeEnv === 'production';
  return !isProdBuild && namespace !== 'beta';
}

// now + 15 minutes on TEST, now + 1 hour on BETA/PROD, seconds zeroed (UTC).
export function gachaDefaultStartDate(nowMs: number, testBackend: boolean): number {
  const start = nowMs + (testBackend ? 15 * MINUTE_MS : HOUR_MS);
  return start - (start % MINUTE_MS);
}

// start + 7 hours on TEST, start + 7 days on BETA/PROD.
export function gachaDefaultStopDate(startMs: number, testBackend: boolean): number {
  return startMs + (testBackend ? 7 * HOUR_MS : 7 * 24 * HOUR_MS);
}

// Not permanent, and the start push explicitly off (the server reads a missing value as on).
export function withGachaNewCampaignDefaults(
  draft: GachaCampaignDraft,
  nowMs: number,
  testBackend: boolean
): GachaCampaignDraft {
  const startDate = gachaDefaultStartDate(nowMs, testBackend);
  return {
    ...draft,
    startDate,
    stopDate: gachaDefaultStopDate(startDate, testBackend),
    startNotificationEnabled: false,
  };
}

export function toGachaCampaignDraft(campaign: GachaCampaignData): GachaCampaignDraft {
  return {
    id: campaign.id,
    name: campaign.name,
    enabled: campaign.enabled,
    namespace: campaign.namespace,
    startDate: toEpochMs(campaign.startDate) ?? Date.now(),
    stopDate: campaign.permanent ? null : toEpochMs(campaign.stopDate),
    showCountdown: campaign.showCountdown,
    startNotificationEnabled: campaign.startNotificationEnabled,
    matcher: toGachaMatcherDraft(campaign.matcher),
    boxes: campaign.boxes.map(toGachaBoxDraft),
  };
}

// ----- draft → server ----------------------------------------------------------------------------------------------

// Comma-separated ids as sent (the shared EventMatchers split, minus empty entries).
export function gachaUserIdList(text: string): string[] {
  return (userIdsStringToArray(text) ?? []).filter(Boolean);
}

// The level as sent: null when empty, NaN when not a number (validation reports it, the save is blocked).
export function gachaMatcherLevel(matcher: GachaMatcherDraft): number | null {
  const level = matcher.userLevel.trim();
  return level ? Number(level) : null;
}

// Spend tiers in the option order; every tier ticked is sent as All (Sale Event's rule: an explicit full list would
// fail closed for payers whose tier is not synced yet).
export function gachaMatcherSpendTiers(matcher: GachaMatcherDraft): UserSpendTier[] {
  const selected = GACHA_SPEND_TIER_OPTIONS.map(({ tier }) => tier).filter((tier) => matcher.spendTiers.includes(tier));
  return selected.length === GACHA_SPEND_TIER_OPTIONS.length ? [] : selected;
}

export function toGachaMatcherInput(matcher: GachaMatcherDraft): GachaMatcherInput {
  const version = matcher.appVersion.trim();
  return {
    userMinCreationDate: matcher.userMinCreationDate,
    userMaxCreationDate: matcher.userMaxCreationDate,
    // Both null = no build version criterion.
    appVersionOperator: version ? matcher.appVersionOperator : null,
    appVersion: version || null,
    userLevel: gachaMatcherLevel(matcher),
    subscriptionTier: matcher.subscriptionTier,
    userIds: gachaUserIdList(matcher.userIds),
    spendTiers: gachaMatcherSpendTiers(matcher),
    maestroOnly: matcher.maestroOnly,
    excludedUserIds: gachaUserIdList(matcher.excludedUserIds),
  };
}

// One line per filter in use, for the collapsed matcher header and the "Same as campaign" summary. Empty = no filter.
export function describeGachaMatcherFilters(matcher: GachaMatcherDraft): string[] {
  const filters: string[] = [];
  if (matcher.userMinCreationDate != null) {
    filters.push(`registered from ${formatUtc(matcher.userMinCreationDate)}`);
  }
  if (matcher.userMaxCreationDate != null) {
    filters.push(`registered until ${formatUtc(matcher.userMaxCreationDate)}`);
  }
  const version = matcher.appVersion.trim();
  if (version) {
    filters.push(`build ${GACHA_VERSION_OPERATOR_LABELS[matcher.appVersionOperator]} ${version}`);
  }
  const level = gachaMatcherLevel(matcher);
  if (level != null) {
    filters.push(`level ≥ ${matcher.userLevel.trim()}`);
  }
  if (matcher.subscriptionTier) {
    filters.push(`subscription ${matcher.subscriptionTier}`);
  }
  const userIds = gachaUserIdList(matcher.userIds);
  if (userIds.length > 0) {
    filters.push(`${userIds.length} user id${userIds.length > 1 ? 's' : ''} only`);
  }
  const spendTiers = gachaMatcherSpendTiers(matcher);
  if (spendTiers.length > 0) {
    const labels = GACHA_SPEND_TIER_OPTIONS.filter(({ tier }) => spendTiers.includes(tier)).map(({ label }) => label);
    filters.push(`spend: ${labels.join(', ')}`);
  }
  if (matcher.maestroOnly) {
    filters.push('Only Maestro');
  }
  const excluded = gachaUserIdList(matcher.excludedUserIds);
  if (excluded.length > 0) {
    filters.push(`${excluded.length} user id${excluded.length > 1 ? 's' : ''} excluded`);
  }
  return filters;
}

// Only the field that names the item is sent for the reward's type.
export function toGachaPoolRowInputs(rows: ReadonlyArray<GachaPoolRowDraft>): GachaPoolRowInput[] {
  return rows.map((row) => {
    const { type } = row.reward;
    const usesItemId =
      type === GachaRewardType.CURRENCY_BOOST ||
      type === GachaRewardType.CURRENCY_CATEGORY_FRAGMENT ||
      type === GachaRewardType.CONTENT_PACK_REWARD ||
      type === GachaRewardType.CURRENCY_VANITY_PIECE;
    return {
      reward: {
        type,
        itemId: usesItemId ? (row.reward.itemId ?? '').trim() || null : null,
        unlimitedBoostType:
          type === GachaRewardType.CURRENCY_UNLIMITED_BOOST ? row.reward.unlimitedBoostType ?? null : null,
      },
      amountMode: row.amountMode,
      // Only the active mode's fields (PRA's rule).
      ...(row.amountMode === GachaAmountMode.EXACT
        ? { amount: row.amount }
        : { minAmount: row.minAmount, maxAmount: row.maxAmount }),
      weight: row.weight,
    };
  });
}

export function toGachaCountRowInputs(rows: ReadonlyArray<GachaCountRowDraft>): GachaCountRowInput[] {
  return rows.map((row) => ({ count: row.count, weight: row.weight }));
}

function toGachaMysteryInput(mystery: GachaMysteryDraft): GachaMysteryInput {
  return {
    enabled: mystery.enabled,
    matcher: mystery.inherit
      ? { inherit: true, criteria: null }
      : { inherit: false, criteria: toGachaMatcherInput(mystery.criteria) },
    firstRatePercent: mystery.firstRatePercent,
    decreasePercent: mystery.decreasePercent,
    minRatePercent: mystery.minRatePercent,
    recoveryMode: mystery.recoveryMode,
    recoveryIntervalSeconds: mystery.recoveryMode === GachaRecoveryMode.GLOBAL ? mystery.recoveryIntervalSeconds : null,
    pool: toGachaPoolRowInputs(mystery.pool),
  };
}

export function toGachaBoxInput(box: GachaBoxDraft): GachaBoxInput {
  const isPaid = box.type === GachaBoxType.PAID;
  return {
    id: box.id,
    tier: box.tier,
    type: box.type,
    claimLimit: box.claimLimit,
    refreshMode: box.refreshMode,
    refreshIntervalSeconds: box.refreshMode === GachaRefreshMode.CUSTOM ? box.refreshIntervalSeconds : null,
    // A FREE box cannot carry a price or a discount.
    price: isPaid ? { currency: box.priceCurrency, amount: box.priceAmount } : null,
    discount:
      isPaid && box.discountEnabled
        ? { percent: box.discountPercent, startDate: box.discountStartDate, stopDate: box.discountStopDate }
        : null,
    countTable: toGachaCountRowInputs(box.countTable),
    rewards: toGachaPoolRowInputs(box.rewards),
    allowDuplicates: box.allowDuplicates,
    reminderEnabled: box.reminderEnabled,
    mystery: box.mystery ? toGachaMysteryInput(box.mystery) : null,
  };
}

export function toUpdateGachaCampaignInput(draft: GachaCampaignDraft): UpdateGachaCampaignInput {
  return {
    id: draft.id,
    name: draft.name.trim(),
    enabled: draft.enabled,
    namespace: draft.namespace,
    startDate: draft.startDate,
    stopDate: draft.stopDate,
    showCountdown: draft.showCountdown,
    startNotificationEnabled: draft.startNotificationEnabled,
    matcher: toGachaMatcherInput(draft.matcher),
    boxes: draft.boxes.map(toGachaBoxInput),
  };
}

// Chance of a row = weight / total x 100 (the first selection), as the server derives it.
export function gachaChancePercents(weights: ReadonlyArray<number>): number[] {
  const safe = weights.map((weight) => (Number.isFinite(weight) && weight > 0 ? weight : 0));
  const total = safe.reduce((sum, weight) => sum + weight, 0);
  return total > 0 ? safe.map((weight) => (weight / total) * 100) : safe.map(() => 0);
}

export function formatChance(percent: number): string {
  return `${(Math.round(percent * 100) / 100).toFixed(2)}%`;
}

export const GACHA_BOOST_TYPES: CurrencyBoostType[] = Object.values(CurrencyBoostType);
export const GACHA_UNLIMITED_BOOST_TYPES: CurrencyUnlimitedBoostType[] = Object.values(CurrencyUnlimitedBoostType);

// ---------------------------------------------------------------------------------------------------------------------
// Running-edit summary: what a save changes, in words, for the confirmation of a RUNNING campaign. Pure.
// ---------------------------------------------------------------------------------------------------------------------

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

const describeEnd = (stopDate: number | null) => (stopDate == null ? 'permanent' : formatUtc(stopDate));

// The campaign-start push toggle is frozen once the campaign has started (the server refuses the change with
// GACHA_ADMIN_START_NOTIFICATION_LOCKED). Same rule as the server: started = the saved AND the edited start are
// both past, so a fresh campaign (created with start = now) can still be set up for a future start.
export function hasGachaCampaignStarted(savedStartMs: number, editedStartMs: number, nowMs: number): boolean {
  return nowMs >= savedStartMs && nowMs >= editedStartMs;
}

// The push line of the save confirmation for a campaign that has not started yet.
export function gachaStartPushSaveNotice(startNotificationEnabled: boolean): string {
  return startNotificationEnabled
    ? 'This campaign will send a start notification to all matching players when it begins.'
    : 'This campaign will not send a start notification. Once the campaign starts, this can no longer be changed.';
}

export function describeGachaRunningEditChanges(saved: GachaCampaignDraft, edited: GachaCampaignDraft): string[] {
  const changes: string[] = [];
  if (saved.enabled && !edited.enabled) {
    changes.push('Disables the campaign: it disappears for every player.');
  }
  if (saved.name.trim() !== edited.name.trim()) {
    changes.push(`Renames the campaign to "${edited.name.trim()}".`);
  }
  if (saved.startDate !== edited.startDate) {
    changes.push(
      `Moves the start from ${formatUtc(saved.startDate)} to ${formatUtc(edited.startDate)} ` +
        '(this also moves the mystery global recovery moments).'
    );
  }
  if (saved.stopDate !== edited.stopDate) {
    changes.push(`Changes the end from ${describeEnd(saved.stopDate)} to ${describeEnd(edited.stopDate)}.`);
  }
  if (saved.namespace !== edited.namespace) {
    changes.push(`Changes the namespace from ${saved.namespace} to ${edited.namespace}.`);
  }
  if (!same(toGachaMatcherInput(saved.matcher), toGachaMatcherInput(edited.matcher))) {
    changes.push('Changes who sees the campaign (matcher): players may gain or lose its boxes.');
  }
  if (saved.showCountdown !== edited.showCountdown) {
    changes.push(`Turns the in-shop countdown ${edited.showCountdown ? 'on' : 'off'}.`);
  }
  if (saved.startNotificationEnabled !== edited.startNotificationEnabled) {
    changes.push(`Turns the campaign-start push ${edited.startNotificationEnabled ? 'on' : 'off'}.`);
  }

  const editedById = new Map(edited.boxes.filter((box) => box.id).map((box) => [box.id as string, box]));
  saved.boxes.forEach((savedBox, index) => {
    const label = `Box ${index + 1} (${savedBox.tier})`;
    const editedBox = savedBox.id ? editedById.get(savedBox.id) : undefined;
    if (!editedBox) {
      changes.push(`Removes ${label}: its players' claims and pity are orphaned.`);
      return;
    }
    const before = toGachaBoxInput(savedBox);
    const after = toGachaBoxInput(editedBox);
    const parts: string[] = [];
    if (before.tier !== after.tier || before.type !== after.type) {
      parts.push(`tier/type ${before.tier}/${before.type} → ${after.tier}/${after.type}`);
    }
    if (before.claimLimit !== after.claimLimit) {
      parts.push(`claim limit ${before.claimLimit} → ${after.claimLimit}`);
    }
    if (before.refreshMode !== after.refreshMode || before.refreshIntervalSeconds !== after.refreshIntervalSeconds) {
      parts.push('refresh');
    }
    if (!same(before.price, after.price) || !same(before.discount, after.discount)) {
      parts.push('price or discount');
    }
    if (!same(before.countTable, after.countTable)) {
      parts.push('rewards per opening');
    }
    if (!same(before.rewards, after.rewards) || before.allowDuplicates !== after.allowDuplicates) {
      parts.push('reward table');
    }
    if (!same(before.mystery, after.mystery)) {
      parts.push('mystery reward');
    }
    if (before.reminderEnabled !== after.reminderEnabled) {
      parts.push(`reminders ${after.reminderEnabled ? 'on' : 'off'}`);
    }
    if (parts.length > 0) {
      changes.push(`Edits ${label}: ${parts.join(', ')}.`);
    }
  });
  const added = edited.boxes.filter((box) => !box.id).length;
  if (added > 0) {
    changes.push(`Adds ${added} new box${added === 1 ? '' : 'es'}.`);
  }
  const savedOrder = saved.boxes.map((box) => box.id).filter((id) => id && editedById.has(id));
  const editedOrder = edited.boxes.map((box) => box.id).filter((id) => id && savedOrder.includes(id));
  if (!same(savedOrder, editedOrder)) {
    changes.push('Reorders the boxes in the carousel.');
  }
  return changes;
}
